import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Sparkles, Loader2, Upload as UploadIcon, Trash2, ImageIcon } from "lucide-react";

const BLANK = {
    title: "",
    description: "",
    subject: "",
    level: "beginner",
    price: 0,
    price_international: "",
    instructor_id: "__none__",
    thumbnail_url: "",
    compliance_guidelines: "",
};

/**
 * Dialog for editing core program details: title, description, subject,
 * level, price, instructor. Used by the admin Programs page (full access)
 * and by the instructor Program Editor (instructor field locked). The
 * chapters/lessons are managed elsewhere — this keeps the form short and
 * focused.
 */
export default function ProgramEditDialog({
    open,
    onOpenChange,
    program,
    instructors = [],
    onSaved,
    endpoint,
    lockInstructor = false,
}) {
    const [form, setForm] = useState(BLANK);
    const [saving, setSaving] = useState(false);
    const [aiPrompt, setAiPrompt] = useState("");
    const [aiBusy, setAiBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (open && program) {
            setForm({
                title: program.title || "",
                description: program.description || "",
                subject: program.subject || "",
                level: program.level || "beginner",
                price: Number.isFinite(program.price) ? program.price : 0,
                price_international:
                    program.price_international == null
                        ? ""
                        : String(program.price_international),
                instructor_id: program.instructor_id || "__none__",
                thumbnail_url: program.thumbnail_url || "",
                compliance_guidelines: program.compliance_guidelines || "",
            });
            setAiPrompt("");
        }
    }, [open, program]);

    const save = async () => {
        if (!form.title.trim()) {
            toast.error("Program title is required");
            return;
        }
        if (!form.description.trim()) {
            toast.error("Description is required");
            return;
        }
        if (!form.subject.trim()) {
            toast.error("Subject is required");
            return;
        }
        const priceNum = Number(form.price);
        if (!Number.isFinite(priceNum) || priceNum < 0) {
            toast.error("Domestic tuition must be a number ≥ 0");
            return;
        }
        let priceIntlNum = null;
        if (form.price_international !== "" && form.price_international != null) {
            priceIntlNum = Number(form.price_international);
            if (!Number.isFinite(priceIntlNum) || priceIntlNum < 0) {
                toast.error("International tuition must be a number ≥ 0");
                return;
            }
        }
        setSaving(true);
        try {
            const payload = {
                title: form.title.trim(),
                description: form.description.trim(),
                subject: form.subject.trim(),
                level: form.level,
                price: priceNum,
                price_international: priceIntlNum,
                thumbnail_url: form.thumbnail_url || null,
                compliance_guidelines: form.compliance_guidelines.trim() || null,
            };
            if (!lockInstructor) {
                payload.instructor_id =
                    form.instructor_id === "__none__" ? null : form.instructor_id;
            }
            const url = endpoint || `/admin/programs/${program.id}`;
            await api.patch(url, payload);
            toast.success("Program updated");
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const onUploadFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
            toast.error("Please pick a PNG, JPG, or WEBP image.");
            return;
        }
        if (file.size > 3 * 1024 * 1024) {
            toast.error("Image is too large (max 3 MB). Compress it first.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/thumbnail", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, thumbnail_url: data.url }));
            toast.success("Thumbnail uploaded.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Upload failed.",
            );
        } finally {
            setUploading(false);
        }
    };

    const onAiGenerate = async () => {
        const prompt = aiPrompt.trim();
        if (prompt.length < 6) {
            toast.error(
                "Describe the image you want — e.g. 'a calm office desk with a laptop and notebook'.",
            );
            return;
        }
        setAiBusy(true);
        const t = toast.loading("Drafting an image… ~20-40 s");
        try {
            const { data } = await api.post("/ai/thumbnail/generate", {
                prompt,
                program_title: form.title || program?.title,
            });
            setForm((f) => ({ ...f, thumbnail_url: data.url }));
            toast.success(
                "Image generated. Save changes if you like it, or click 'Try again'.",
                { id: t },
            );
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "AI image generation failed.",
                { id: t },
            );
        } finally {
            setAiBusy(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] flex flex-col p-0"
                data-testid="program-edit-dialog"
            >
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Pencil className="h-4 w-4 text-primary" />
                        Edit program details
                    </DialogTitle>
                    <DialogDescription>
                        Change the title, description, pricing, or assigned instructor.
                        Chapters &amp; lessons are edited from the curriculum page.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                    <div>
                        <Label htmlFor="prog-title">Program name</Label>
                        <Input
                            id="prog-title"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            placeholder="e.g. Office Administration Diploma"
                            data-testid="program-edit-title"
                        />
                    </div>

                    <div>
                        <Label htmlFor="prog-desc">Description</Label>
                        <Textarea
                            id="prog-desc"
                            rows={4}
                            value={form.description}
                            onChange={(e) =>
                                setForm({ ...form, description: e.target.value })
                            }
                            placeholder="Short catalog description students see on the public page."
                            data-testid="program-edit-description"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="prog-subject">Subject</Label>
                            <Input
                                id="prog-subject"
                                value={form.subject}
                                onChange={(e) =>
                                    setForm({ ...form, subject: e.target.value })
                                }
                                placeholder="e.g. Business"
                                data-testid="program-edit-subject"
                            />
                        </div>
                        <div>
                            <Label>Level</Label>
                            <Select
                                value={form.level}
                                onValueChange={(v) => setForm({ ...form, level: v })}
                            >
                                <SelectTrigger data-testid="program-edit-level-trigger">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="beginner">Beginner</SelectItem>
                                    <SelectItem value="intermediate">Intermediate</SelectItem>
                                    <SelectItem value="advanced">Advanced</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="rounded-md border border-amber-500/30 bg-amber-50/40 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="prog-compliance" className="text-xs uppercase tracking-[0.18em] text-amber-800 font-semibold m-0">
                                AI Compliance Guidelines
                            </Label>
                            <span className="text-[10px] text-muted-foreground">
                                Optional · PTIB
                            </span>
                        </div>
                        <Textarea
                            id="prog-compliance"
                            rows={5}
                            value={form.compliance_guidelines}
                            onChange={(e) =>
                                setForm({ ...form, compliance_guidelines: e.target.value })
                            }
                            placeholder="Paste the PTIB-approved learning outcomes, scope statement, or approved-program-document excerpts here. Anything you write is prepended to every AI lesson / outline / quiz prompt for this program so generated content stays in scope."
                            data-testid="program-edit-compliance-guidelines"
                            className="bg-background"
                        />
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                            Used as a high-priority instruction block in front of every AI
                            content prompt for this program. Empty = no constraint.
                        </p>
                    </div>

                    <div className="rounded-md border border-border bg-muted/20 p-3 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                                Thumbnail
                            </div>
                            {form.thumbnail_url ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                        setForm((f) => ({ ...f, thumbnail_url: "" }))
                                    }
                                    className="text-muted-foreground"
                                    data-testid="program-edit-thumbnail-clear"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            ) : null}
                        </div>

                        <div className="grid grid-cols-[120px_1fr] gap-3 items-start">
                            <div
                                className="aspect-square rounded-md overflow-hidden border border-border bg-background flex items-center justify-center"
                                data-testid="program-edit-thumbnail-preview"
                            >
                                {form.thumbnail_url ? (
                                    <img
                                        src={form.thumbnail_url}
                                        alt="Thumbnail preview"
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <ImageIcon className="h-7 w-7 text-muted-foreground" />
                                )}
                            </div>

                            <div className="space-y-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    onChange={onUploadFile}
                                    className="hidden"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    className="w-full justify-start gap-1.5"
                                    data-testid="program-edit-thumbnail-upload"
                                >
                                    {uploading ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <UploadIcon className="h-3.5 w-3.5" />
                                    )}
                                    {uploading ? "Uploading…" : "Upload my own image"}
                                </Button>

                                <div className="space-y-1.5">
                                    <Label className="text-xs">
                                        — or describe the image and let AI draft it
                                    </Label>
                                    <Textarea
                                        rows={2}
                                        value={aiPrompt}
                                        onChange={(e) => setAiPrompt(e.target.value)}
                                        placeholder="e.g. a clean office desk with laptop and notebook, soft warm light"
                                        className="text-sm"
                                        data-testid="program-edit-thumbnail-prompt"
                                    />
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={onAiGenerate}
                                        disabled={aiBusy}
                                        className="ai-shimmer w-full justify-start gap-1.5"
                                        data-testid="program-edit-thumbnail-ai"
                                    >
                                        {aiBusy ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Sparkles className="h-3.5 w-3.5" />
                                        )}
                                        <span className="text-xs font-semibold">
                                            {aiBusy
                                                ? "Drafting…"
                                                : form.thumbnail_url
                                                    ? "Try again with this prompt"
                                                    : "AI: Generate image"}
                                        </span>
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <p className="text-[11px] text-muted-foreground">
                            Square images look best (1:1). Click <strong>Save changes</strong>{" "}
                            below to keep the thumbnail you see.
                        </p>
                    </div>

                    <div className="rounded-md border border-border bg-muted/20 p-3 space-y-3">
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                            Tuition fees
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="prog-price">Domestic (CAD)</Label>
                                <Input
                                    id="prog-price"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.price}
                                    onChange={(e) =>
                                        setForm({ ...form, price: e.target.value })
                                    }
                                    data-testid="program-edit-price"
                                />
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    Students residing in Canada.
                                </p>
                            </div>
                            <div>
                                <Label htmlFor="prog-price-intl">International (CAD)</Label>
                                <Input
                                    id="prog-price-intl"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={form.price_international}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            price_international: e.target.value,
                                        })
                                    }
                                    placeholder="Leave empty to use domestic"
                                    data-testid="program-edit-price-international"
                                />
                                <p className="text-[11px] text-muted-foreground mt-1">
                                    Overseas applicants. Falls back to the domestic fee if empty.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Label>Instructor</Label>
                        {lockInstructor ? (
                            <div
                                className="h-10 px-3 flex items-center rounded-md border border-input bg-muted/40 text-sm text-muted-foreground"
                                data-testid="program-edit-instructor-locked"
                            >
                                {instructors.find((u) => u.id === form.instructor_id)?.name
                                    || instructors.find((u) => u.id === form.instructor_id)?.email
                                    || "Unassigned"}
                                <span className="ml-auto text-[10px] uppercase tracking-[0.14em]">
                                    Admin-only
                                </span>
                            </div>
                        ) : (
                            <Select
                                value={form.instructor_id}
                                    onValueChange={(v) =>
                                        setForm({ ...form, instructor_id: v })
                                    }
                                >
                                    <SelectTrigger data-testid="program-edit-instructor-trigger">
                                        <SelectValue placeholder="Unassigned" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">Unassigned</SelectItem>
                                        {instructors.map((u) => (
                                            <SelectItem
                                                key={u.id}
                                                value={u.id}
                                                data-testid={`program-edit-instructor-${u.id}`}
                                            >
                                                {u.name || u.email}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                    </div>
                </div>

                <DialogFooter className="px-6 py-4 border-t border-border flex-shrink-0 bg-background">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="program-edit-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="program-edit-save"
                    >
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
